import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { AuthService, BreadcrumbService, HomeService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { 
  GetHomeConfigSlimIdRequest, GetHomeConfigSlimIdResponse, HomeIfcFile, ListHomesByCompanyResponse,
  GetHomeConfigRequest, GetHomeConfigResponse, ListHomesByCompanyRequest, ListIfcFilesByHomeIdRequest, ListIfcFilesByHomeIdResponse, Home, HomeIfcFileType 
} from 'situview-webapi-client';
import { HomeConfig } from 'siturenderservice-webapi-client';
import { Validators, FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { ProductName } from 'situ-warpapi-client';
import { HomeConfigSlim } from 'src/app/shared/models/home-config-slim';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Router } from '@angular/router';

@Component({
  selector: 'home-config-slim',
  templateUrl: './home-config-slim.component.html',
  styleUrls: ['./home-config-slim.component.less']
})
export class HomeConfigSlimComponent extends SituBaseComponent implements OnChanges, AfterViewInit {
  @Input() incomingHomeConfigId: string | undefined;
  @Input() lockedStaging: boolean = false;
  @Input() product: ProductName = ProductName.Panorama;
  @Input() includePlaceholders: boolean = false;

  @Output() homeConfigIdUpdates: EventEmitter<string> = new EventEmitter();
  @Output() homeIdUpdates: EventEmitter<string> = new EventEmitter();
  @Output() homeConfigUpdates: EventEmitter<HomeConfig> = new EventEmitter();

  initialised: boolean = false;
  initialHomeConfigId: string | undefined;
  loadingHomeConfig: boolean = false;

  homeControl = new FormControl('', Validators.required);
  masterFileControl = new FormControl('', Validators.required);
  private _selectedIfcFiles: HomeIfcFile[] = [];
  get selectedIfcFiles(): HomeIfcFile[] {
    return this._selectedIfcFiles;
  }
  set selectedIfcFiles(files: HomeIfcFile[]) {
    this._selectedIfcFiles = files;
    this.emitHomeConfigUpdates();
  }
  
  homes: Home[] = [];
  filteredHomes: Observable<Home[]> | undefined;
  selectedHome: Home | undefined;
  masterIfcFiles: HomeIfcFile[] = [];
  optionIfcFiles: HomeIfcFile[] = [];
  
  homeConfig: HomeConfig | undefined;

  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private clipboard: Clipboard,
    private homeService: HomeService,
    private router: Router) {
      super(authService, stateService);
      if (this.incomingHomeConfigId) this.initialHomeConfigId = this.incomingHomeConfigId;
    }

  protected onSelectedCompanyChange(): void {
    this.loadHomes().then(x => {
      this.clearHome();
    });
  }
  
  ngOnInit(): void {
    super.ngOnInit();
    this.loadHomes().then(x => {
      this.clearHome();
      this.initialised = true;
    });
    this.filteredHomes = this.homeControl.valueChanges
      .pipe(
        startWith(''),
        map(value => value ? (typeof value === 'string' ? value : value.name) : ''),
        map(name => name ? this._filterHomes(name) : this.homes.slice())
      );

    this._subscriptions.push(this.homeControl.valueChanges.subscribe((selectedHome: Home) => {
      if (selectedHome && typeof selectedHome !== 'string') {
        this.selectedHome = selectedHome;
        this.emitHomeUpdates(selectedHome.id!);
        this.loadIfcFiles(selectedHome.id!)
          .then(x => {
            this.emitHomeConfigUpdates();
          });
      }
    }));

    this._subscriptions.push(this.masterFileControl.valueChanges.subscribe(() => {
      if (!this.loadingHomeConfig) this.emitHomeConfigUpdates();
    }));
  }

  ngOnDestroy(): void {
    super.ngOnInit();
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.incomingHomeConfigId && !changes.incomingHomeConfigId.isFirstChange() && changes.incomingHomeConfigId?.currentValue) {
      this.getHomeConfigById(changes.incomingHomeConfigId.currentValue)
        .then((response: GetHomeConfigResponse) => {
          if (response.success && response?.homeConfig) {
            this.homeConfig = response.homeConfig;
            let home = this.homes.find(x => x.id === response.homeId);
            if (home) {
              this.homeControl.setValue(home);
            }
          }
        });
    }
  }

  ngAfterViewInit(){
    if (this.initialHomeConfigId) {
      this.waitUntilTrue(() => this.initialised === true, 5000).then(() => {
        this.getHomeConfigById(this.initialHomeConfigId!)
          .then((response: GetHomeConfigResponse) => {
            if (response.success && response?.homeConfig) {
              this.homeConfig = response.homeConfig;
              let home = this.homes.find(x => x.id === response.homeId);
              if (home) {
                this.homeControl.setValue(home);
              }
            }
          });
      });
    }
  }

  browse(homeId?: string) {
    if (homeId) {
      if (this.product === ProductName.Facade) {
        this.router.navigate(['/situ-facade/browse', this.product], { queryParams: { home: homeId } });
      } else if (this.product === ProductName.Panorama) {
        this.router.navigate(['/situ-view/browse', this.product], { queryParams: { home: homeId } });
      }
    }
  }

  displayHome(home: Home): string {
    return home && home.name ? home.name : '';
  }

  displayIfcFile(ifcFile: HomeIfcFile): string {
    return ifcFile && ifcFile.name ? ifcFile.name : '';
  }

  private _filterHomes(value: string): Home[] {
    const filterValue = value.toLowerCase();
    return this.homes.filter(home => home.name!.toLowerCase().includes(filterValue));
  }

  clearHome(): void {
    this.homeControl.setValue('');
    this.homeControl.reset();
  
    this.selectedHome = undefined;
  
    this.clearMasterFile();
    this.clearOptions();
  }
  
  clearMasterFile(): void {
    this.masterFileControl.setValue('');
    this.masterFileControl.reset();
  
    this.masterIfcFiles = [];
  }
  
  clearOptions(): void {
    this.selectedIfcFiles = [];
  }

  removeOption(option: HomeIfcFile): void {
    const index = this.selectedIfcFiles.indexOf(option);
  
    if (index >= 0) {
      this.selectedIfcFiles = this.selectedIfcFiles.splice(index, 1);
      //this.cdr.detectChanges();
    }
  }

  loadIfcFiles(homeId: string) {
    if (!this.selectedCompany?.code || !this.homeControl.value) {
      return Promise.resolve();
    }
    this.loading = true;

    var request = new ListIfcFilesByHomeIdRequest();
    request.companyId = this.selectedCompany.id;
    request.homeId = homeId;

    return this.homeService.ListIfcFilesByHomeId(request)
      .then((response: ListIfcFilesByHomeIdResponse) => {
        if (response.success && response.ifcFiles) {
          let files = response.ifcFiles;
          if (!this.includePlaceholders) {
            files = files.filter((x: HomeIfcFile) => !x.placeholderArtifactId);
          }
          this.masterIfcFiles = files.filter((x: HomeIfcFile) => x.type === HomeIfcFileType.Master);
          this.optionIfcFiles = files.filter((x: HomeIfcFile) => x.type === HomeIfcFileType.Option);

          if (this.homeConfig) {
            let masterFile = this.masterIfcFiles.find(x => x.name?.toLowerCase() === this.homeConfig!.masterFile?.toLowerCase());
            if (masterFile) {
              this.masterFileControl.setValue(masterFile);
            }
  
            if (this.homeConfig?.options) {
              
              this.selectedIfcFiles = this.optionIfcFiles.filter(file => 
                this.homeConfig!.options!.includes(file.name!.toLowerCase())
              );
            }
            this.homeConfig = undefined;
          } else {
            if (this.masterIfcFiles && this.masterIfcFiles.length > 0) {
              this.masterFileControl.setValue(this.masterIfcFiles[0]);
            }
          }
        } else {
          this.showMessage("Failed to load ifc files.");
        }
      })
      .catch((err: any) => {
        this.showMessage("Failed to load ifc files.");
      })
      .finally(() => {
        this.loading = false;
        this.loadingHomeConfig = false;
      });
  }

  getHomeName(styleTemplate: HomeIfcFile) {
    return styleTemplate?.name;
  }

  copyConfig() {
    this.clipboard.copy(JSON.stringify(this.getBasicHomeConfig()));
    this.snackBar.open(`Copied to clipboard.`, "Close", {
      duration: 5000,
      panelClass: ['copied-snackbar']
    });
  }

  private loadHomes() {
    if (!this.selectedCompany?.code) {
      return Promise.resolve();
    }
    this.loading = true;
    
    var request = new ListHomesByCompanyRequest();
    request.companyId = this.selectedCompany.id;
    request.pageSize = 1000;
    request.isHidden = false;

    return this.homeService.ListHomesByCompany(request)
      .then((homes: Home[]) => {
        this.homes = this.includePlaceholders ? homes : homes.filter(h => !(h as any).isPlaceholder);
        this.homeControl.setValue('');
      })
      .catch((err: any) => {
        this.showMessage("Failed to load homes.");
      })
      .finally(() => {
        this.loading = false;
      });
  };

  public getHomeConfigById(homeConfigId: string) {
    this.loadingHomeConfig = true;
    let request = new GetHomeConfigRequest();
    request.homeConfigId = homeConfigId;

    return this.homeService.GetHomeConfig(request);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  getBasicHomeConfig(): HomeConfig {
    let homeConfig = new HomeConfig();
    homeConfig.builder = this.selectedCompany?.code;
    homeConfig.home = this.homeControl?.value?.name;
    homeConfig.masterFile = this.masterFileControl?.value?.name;

    homeConfig.options = [];
    this.selectedIfcFiles.map(ifcFile => ifcFile.name!).forEach(option => {
      homeConfig.options?.push(option);
    });

    return homeConfig;
  }

  getHomeConfig(): HomeConfigSlim | undefined {
    let homeConfig = this.getBasicHomeConfig();
    if (homeConfig.home && homeConfig.masterFile) {
      return new HomeConfigSlim(homeConfig.home, homeConfig.masterFile, homeConfig.options ?? []);
    }
  }

  getHomeConfigId() {
    let request = new GetHomeConfigSlimIdRequest();
    request.homeConfig = this.getBasicHomeConfig();
    request.companyId = this.selectedCompany?.id;

    return this.homeService.GetHomeConfigId(request)
      .then((response: GetHomeConfigSlimIdResponse) => {
        if (!response?.success) {
          this.showMessage("Home Config is invalid.");
        } else {
          this.homeConfigUpdates.emit(request.homeConfig);
          return response.homeConfigId;
        }
      });
  }

  private emitHomeUpdates(homeId: string) {
    if (homeId) this.homeIdUpdates.emit(homeId);
  }

  private emitHomeConfigUpdates() {
    if (this.homeControl.valid && this.homeControl.value?.name && 
        this.masterFileControl.valid && this.masterFileControl.value?.name) {
      if (!this.loadingHomeConfig) {
        // Add small delay to prevent race conditions with rapid state changes
        setTimeout(() => {
          if (!this.loadingHomeConfig) { // Double-check after delay
            this.getHomeConfigId()
            .then((homeConfigId: string | undefined) => {
              if (homeConfigId) {
                this.homeConfigIdUpdates.emit(homeConfigId);
              }
            }).catch((err: any) => {
              console.error('Failed to get home config ID', err);
            });
          }
        }, 50);
      }
    } else {
      this.homeConfigIdUpdates.emit("");
    }
  }

  private waitUntilTrue(condition: () => boolean, timeout: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(() => {
        if (condition()) {
          clearInterval(interval);
          resolve();
        } else {
          timeout -= 1000;
          if (timeout <= 0) {
            clearInterval(interval);
            reject(new Error("Timeout"));
          }
        }
      }, 1000);
    });
  }
}
